/**
 * @file ProtectedRoute component to wrap pages that require authentication
 * does the same as middleware in Next.js but with Clerk's react hooks
 *
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useAgentInstancesList } from '@/hooks/useInitializeApp';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';
import { logInfo, logger } from '@/lib/logger';
import { APP_ROUTES } from '@/constants';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute ({ children }: ProtectedRouteProps) {
  const { isLoaded, userId } = useAuth();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const { isComplete: hasCompletedOnboarding, isLoading: isOnboardingLoading } = useOnboardingStatus();

  // Initialize agent instances list only when user is authenticated
  useAgentInstancesList();

  useEffect(() => {
    /**
     * Check authentication status and onboarding completion.
     *
     * Redirects to sign-in if not authenticated, or to onboarding
     * if authentication is complete but onboarding is not.
     */
    const checkAuth = async () => {
      try {
        logInfo('ProtectedRoute: Starting auth check', {
          isLoaded,
          userId: userId ? 'present' : 'missing',
          isOnboardingLoading,
          pathname: window.location.pathname,
        });

        // Wait for auth to be loaded
        if (!isLoaded) {
          logInfo('ProtectedRoute: Waiting for auth to load');
          return;
        }

        if (!userId) {
          logInfo('ProtectedRoute: No userId, redirecting to sign-in');
          const current = window.location.pathname + window.location.search;
          navigate(`${APP_ROUTES.SIGN_IN}?redirect_url=${encodeURIComponent(current)}`);
          return;
        }

        // Wait for onboarding status to resolve
        if (isOnboardingLoading) {
          logInfo('ProtectedRoute: Waiting for onboarding status check');
          return;
        }

        logInfo('ProtectedRoute: Onboarding status check', {
          hasCompletedOnboarding,
          currentPath: window.location.pathname,
        });

        // If onboarding not completed and not already on onboarding page
        if (!hasCompletedOnboarding && !window.location.pathname.startsWith('/ui/onboarding')) {
          logInfo('ProtectedRoute: Redirecting to onboarding');
          const current = window.location.pathname + window.location.search;
          const existingRedirect = new URLSearchParams(window.location.search).get('redirect_url');
          const forwardRedirect = existingRedirect || current;
          navigate(`${APP_ROUTES.ONBOARDING}?redirect_url=${encodeURIComponent(forwardRedirect)}`);
          return;
        }

        // Auth check complete
        logInfo('ProtectedRoute: Auth check completed successfully');
        setIsChecking(false);
      } catch (error) {
        logger(error as Error, {
          context: 'ProtectedRoute: Error during auth check',
          userId: userId ? 'present' : 'missing',
          pathname: window.location.pathname,
        });

        // On error, redirect to sign-in as a fallback
        navigate(APP_ROUTES.SIGN_IN);
      }
    };

    checkAuth();
  }, [isLoaded, userId, navigate, hasCompletedOnboarding, isOnboardingLoading]);

  // Show loading state while checking auth
  if (isChecking) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin h-8 w-8 border-4 border-orange-500 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  return <>{children}</>;
}
